import React from 'react'
import { useState, useCallback, useEffect, useSelector, useDispatch, useDragAndDrop } from 'hooks'
import { debounce } from 'lodash'
import { contrast } from 'styles'
import descriptions from 'mocks/themes/descriptions'
import StyledThemeEditor from './StyledThemeEditor'

const pairs = {};
[descriptions.normal, descriptions.inverted].forEach(group => {
  Object.keys(group.backgrounds).forEach(key => (pairs[key] = Object.keys(group.texts)[0]))
  Object.keys(group.texts).forEach(key => (pairs[key] = Object.keys(group.backgrounds)[0]))
})

const Contrast = ({ colors, name }) => {
  const { ratio, score } = contrast(colors[name], colors[pairs[name]], /title|cta|highlight/i.test(name))

  return (
    <span className="contrast">
      <span className="ratio">{ratio}</span> <span className="score">{score}</span>
    </span>
  )
}

const styles = (colors, group, name) =>
  !/texts|backgrounds/.test(group)
    ? null
    : {
        color: colors[group == 'texts' ? name : pairs[name]],
        background: colors[group == 'texts' ? pairs[name] : name],
      }

const ifValid = cb => ({ target: { value } }) => value.match(/^#[0-9a-f]{6}$/i) && cb(value)
const format = color => color.replace(/^#(\w)(\w)(\w)$/, '#$1$1$2$2$3$3')

const Item = ({ name, description, group, colors, setColor, type }) => (
  <label className="theme-item" id={`item-${name}`} style={styles(colors, group, name)}>
    <div className="info">
      <strong>{name}</strong> {/texts|backgrounds/.test(group) && <Contrast colors={colors} name={name} />}
      <p className="description">{description}</p>
    </div>
    <input
      type={type}
      name={name}
      size="7"
      defaultValue={format(colors[name])}
      onChange={ifValid(v => setColor({ [name]: v }))}
    />
  </label>
)

const Group = ({ name, items, children, ...rest }) => (
  <div className="theme-group">
    {name && <strong className="theme-group-title">{name}</strong>}
    {children ||
      Object.entries(items).map(([key, description]) => (
        <Item group={name} key={key} name={key} description={description} {...rest} />
      ))}
  </div>
)

const Section = ({ name, items, children, ...rest }) => (
  <section className={`theme-section theme-section-${name}`}>
    <h1 className="theme-section-title">{name}</h1>
    <div className="theme-groups">
      {children || Object.entries(items).map(([key, items]) => <Group key={key} name={key} items={items} {...rest} />)}
    </div>
  </section>
)

const ThemeEditor = () => {
  const enabled = useSelector(s => s.flags.themeEditor) || null
  const [open, setOpen] = useState(true)
  const [text, setText] = useState(false)
  const styles = useSelector(s => s.siteConfig.styles.colors)
  const [colors, setColors] = useState(styles)
  const [detailed, setDetailed] = useState(true)
  const dispatch = useDispatch()
  const changeGlobalColor = useCallback(
    debounce(colors => dispatch({ type: 'CHANGE_COLOR', payload: colors }), 50),
    []
  )
  const changeLocalColor = useCallback(
    debounce(colorInfo => setColors(colors => ({ ...colors, ...colorInfo })), 10),
    []
  )
  const { dragging, dragHandlers } = useDragAndDrop(([file]) => {
    file.text().then(text => {
      try {
        const colors = JSON.parse(text)
        setColors(colors)
        Object.entries(colors).forEach(([name, value]) => {
          document.querySelector(`[name="${name}"]`).value = value
        })
        dispatch({ type: 'CHANGE_THEME', payload: { colors } })
      } catch {
        alert('bad file')
      }
    })
  })

  const setColor = colorInfo => {
    changeGlobalColor(colorInfo)
    changeLocalColor(colorInfo)
  }
  const modes = `${open ? 'open' : 'closed'} ${detailed ? 'long' : 'short'} ${text ? 'text' : 'color'}`
  const downloadHref = `data:text/plain;charset=utf-8,${encodeURIComponent(JSON.stringify(colors, null, 2))}`
  const props = { colors, setColor, type: text ? 'text' : 'color' }

  useEffect(() => {
    setColors(styles)
  }, [enabled])

  return (
    enabled && (
      <StyledThemeEditor colors={colors} className={`${modes} ${dragging ? 'dragging' : ''}`} {...dragHandlers}>
        <div className="actions">
          <i className="keyboard fas fa-keyboard" title="toggle input type" onClick={() => setText(x => !x)} />
          <i className="detailed fas fa-comment-dots" title="toggle details" onClick={() => setDetailed(x => !x)} />
          <i className="fas fa-angle-down" title="toggle editor" onClick={() => setOpen(x => !x)} />
          <a download="theme.json" href={downloadHref}>
            <i className="fas fa-download" title="download theme" onClick={() => setDetailed(x => !x)} />
          </a>
        </div>
        <Section name="normal" items={descriptions.normal} {...props} />
        <Section name="inverted" items={descriptions.inverted} {...props} />
        <Section name="extra">
          <Group items={descriptions.extra} {...props} />
        </Section>
        {dragging && <div className="dragHelper">Drop theme to load</div>}
      </StyledThemeEditor>
    )
  )
}

export default ThemeEditor
